import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/products";
import { Feather, Heart, Sparkles, Users, Award, ArrowRight, Crown } from "lucide-react";

const VALUES = [
  { icon: Feather, t: "Clarity", d: "We simplify the complex so you can focus on what matters." },
  { icon: Award, t: "Integrity", d: "We're honest, transparent, and committed to doing things the right way." },
  { icon: Sparkles, t: "Growth", d: "We believe in continuous improvement and the power of daily progress." },
  { icon: Users, t: "Community", d: "We're here for the journey - together. You're never alone." },
  { icon: Heart, t: "Quality", d: "We never compromise on the materials, design, or experience." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-ivory">
        <div className="container-nc py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="overline mb-5">Designed for clarity. Built for success.</p>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-[1.05]">
              Our Story.
              <br /><span className="italic">Your Journey.</span>
            </h1>
            <p className="mt-6 text-muted_brown max-w-md leading-relaxed">
              Note Console was created to help driven individuals bring intention to their days and clarity to their lives.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/product/life-os-planner" className="btn-primary">Shop the Planner</Link>
              <Link to="/shop" className="btn-outline">Our Planners</Link>
            </div>
          </div>
          <img src="/product/about-premium-diary-nc.jpg" alt="Premium Note Console diary with NC logo" className="rounded-2xl w-full aspect-[5/4] object-cover shadow-medium" loading="eager" fetchPriority="high" />
        </div>
      </section>

      <section className="container-nc py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { t: "Our Mission", d: "Empower you to plan better, live better, and achieve more." },
          { t: "For the Driven", d: "Thoughtful tools for people who value purpose and progress." },
          { t: "Built to Last", d: "Premium quality and timeless design you can rely on every day." },
        ].map((m) => (
          <div key={m.t} className="text-center md:text-left">
            <div className="w-10 h-10 mx-auto md:mx-0 rounded-full gold-border flex items-center justify-center mb-4">
              <Sparkles size={14} className="text-gold" strokeWidth={0} fill="currentColor" />
            </div>
            <h3 className="font-serif text-2xl text-espresso mb-2">{m.t}</h3>
            <p className="text-sm text-muted_brown">{m.d}</p>
          </div>
        ))}
      </section>

      <section className="bg-ivory border-y border-line">
        <div className="container-nc py-20 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-14 items-center">
          <img src="/planner-pages/planner-page-01.jpg" alt="Not Just A Planner cover" className="rounded-2xl w-full aspect-[4/5] object-contain bg-cream" />
          <div>
            <p className="overline mb-4">A note from our founders</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
              Why We Started Note Console
            </h2>
            <div className="mt-8 space-y-6 text-muted_brown leading-relaxed">
              <div className="border-l-2 border-gold pl-5">
                <div className="font-serif text-2xl text-espresso mb-2">Tripti</div>
                <p>
                  Doing an MBA, coupled with two startups, committee pressure, and SIP work, I
                  desperately needed to sort my life out. I needed something to remember my
                  deadlines, reduce the chaos in my mind, and bring a little calm into my very
                  hectic schedule. That is why I created Note Console: a solution to a problem I
                  faced, and one I wanted to share with everyone trying to give their hectic life
                  a proper shape.
                </p>
              </div>
              <div className="border-l-2 border-gold pl-5">
                <div className="font-serif text-2xl text-espresso mb-2">Uttam</div>
                <p>
                  Since childhood, I have always loved writing, planning, and finding better ways
                  to keep life organised. Over time, I noticed that many people shared the same
                  love for putting their thoughts, goals, and dreams on paper. I wanted to create
                  something that felt more meaningful than an ordinary planner - something that
                  could bring clarity, structure, and calm to everyday life.
                </p>
                <p className="mt-3 text-espresso font-medium">
                  That curiosity and passion became Note Console: not just a planner, but a space
                  to organise the life you are building.
                </p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cream border border-line flex items-center justify-center shadow-soft">
                  <Crown size={24} strokeWidth={1.8} className="text-gold" />
                </div>
                <div>
                <div className="text-espresso font-medium">Tripti</div>
                <div className="text-xs text-muted_brown">Co-Founder, Note Console</div>
                </div>
              </div>
              <div className="h-10 w-px bg-line hidden sm:block" />
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-cream border border-line flex items-center justify-center shadow-soft">
                  <Crown size={24} strokeWidth={1.8} className="text-gold" />
                </div>
                <div>
                  <div className="text-espresso font-medium">Uttam</div>
                  <div className="text-xs text-muted_brown">Co-Founder and Developer</div>
                </div>
              </div>
              <div className="ml-auto font-serif italic text-2xl text-gold hidden md:block">Tripti &amp; Uttam</div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-nc py-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="overline mb-4">Thoughtful design. Meaningful impact.</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            Premium materials.
            <br /><span className="italic">Timeless craftsmanship.</span>
          </h2>
          <p className="mt-6 text-muted_brown max-w-md leading-relaxed">
            We believe the tools you use should inspire you every day. That's why we use
            premium materials, refined finishes, and durable binding - so your planner
            can keep up with your ambitions.
          </p>
          <ul className="mt-8 grid grid-cols-2 gap-4 text-sm text-espresso">
            {["Soft Paperback", "Hard Binding", "75-100gsm Premium Quality", "Quality Check", "Digital Access", "Sustainable Packaging"].map((f) => (
              <li key={f} className="flex items-center gap-2 border-b border-line pb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-gold" /> {f}
              </li>
            ))}
          </ul>
        </div>
        <img src={IMAGES.planner_open} alt="Craftsmanship" className="rounded-2xl w-full aspect-square object-cover shadow-medium" />
      </section>

      <section className="bg-ivory border-t border-line">
        <div className="container-nc py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline mb-4">What we stand for</p>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso">Our Values</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {VALUES.map((v) => (
              <div key={v.t} className="bg-cream border border-line rounded-xl p-6 text-center">
                <div className="w-10 h-10 mx-auto rounded-full gold-border flex items-center justify-center mb-4">
                  <v.icon size={14} strokeWidth={1.5} className="text-gold" />
                </div>
                <h4 className="font-serif text-lg text-espresso mb-2">{v.t}</h4>
                <p className="text-xs text-muted_brown leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-nc py-20 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { n: "2026", l: "Founded" },
          { n: "8+", l: "Premium planners" },
          { n: "Pan India", l: "Delivery reach" },
          { n: "4.9/5", l: "Rating" },
        ].map((s) => (
          <div key={s.l}>
            <div className="font-serif text-4xl md:text-5xl text-espresso">{s.n}</div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted_brown mt-2">{s.l}</div>
          </div>
        ))}
      </section>

      <section className="bg-champagne/60">
        <div className="container-nc py-20 text-center max-w-2xl mx-auto">
          <h2 className="font-serif text-4xl md:text-5xl text-espresso">
            Ready to start planning with intention?
          </h2>
          <Link to="/product/life-os-planner" className="btn-primary mt-8">
            Shop the Life OS Planner <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
